import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Select } from 'antd';
import { Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';

import styles from './styles.module.scss';

import { DateFormat } from '../../constants/data';
import { notesApi, useCreateNoteMutation, useDeleteNoteMutation, useEditNoteMutation } from '../../store/api/notes';
import { Note } from './index.jsx';
import { useViewport } from '../../hooks';
import { BreakpointMobile, BreakpointTablet } from '../../constants/ui';
import { getListNotesTypes, getNoteTypes } from '../../utils/notes.jsx';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

const { TextArea } = Input;

const Notes = ({ t, locationId, isImageBox }) => {
  const NoteTypes = getNoteTypes(t);
  const ListNotesTypes = getListNotesTypes(t);
  const { widthViewport } = useViewport();

  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const { selectedDate } = useSelector((state) => state.sensors);
  const { notesList } = useSelector((state) => state.notes);
  const { currentUserRole } = useSelector((state) => state.auth);

  const [createNote, { isSuccess: isCreateNoteSuccess }] = useCreateNoteMutation();
  const [deleteNote, { isSuccess: isDeleteNoteSuccess }] = useDeleteNoteMutation();
  const [editNote, { isSuccess: isEditNoteSuccess }] = useEditNoteMutation();

  const [notesUpdated, setNotesUpdated] = useState(0);
  const [editNoteId, setEditNoteId] = useState('');

  const onCreateNote = () => {
    const note = form.getFieldValue('note');
    const noteType = form.getFieldValue('noteType');

    if (note && noteType) {
      if (editNoteId) {
        editNote({
          noteId: editNoteId,
          locationId,
          date: dayjs(selectedDate).format(DateFormat),
          content: note,
          noteType,
        });
        setEditNoteId('');
      } else {
        createNote({
          locationId,
          date: dayjs(selectedDate).format(DateFormat),
          content: note,
          noteType,
        });
      }

      form.setFieldValue('note', '');
    }
  };

  const onOpenEditingNote = ({ note }) => {
    form.setFieldValue('note', note.content);
    form.setFieldValue('noteType', note.note_type);
    setEditNoteId(note.id);
  };

  const onCloseEditNote = () => {
    form.setFieldValue('note', '');
    setEditNoteId('');
  };

  const onRemoveNote = ({ id }) => {
    deleteNote({ noteId: id });
  };

  useEffect(() => {
    if (isCreateNoteSuccess || isDeleteNoteSuccess || isEditNoteSuccess) {
      dispatch(notesApi.util.resetApiState());
      setNotesUpdated(1);
    }
  }, [isCreateNoteSuccess, isDeleteNoteSuccess, isEditNoteSuccess]);

  useEffect(() => {
    if (currentUserRole !== 'viewer') {
      form.setFieldValue('note', '');
      setEditNoteId('');
    }
  }, [selectedDate]);

  return (
    <div className={styles.notes} key={notesUpdated}>
      {!notesList || notesList.length < 1 || !notesList.find((n) => n.date === selectedDate) ? (
        <div className={`${styles.notesEmpty} ${isImageBox ? styles.notesEmptyPadding : ''}`}>{t('notes.noNotes')}</div>
      ) : (
        ''
      )}
      <div className={styles.notesList}>
        {notesList &&
          notesList
            .filter((n) => n.date === selectedDate)
            .map((note) => (
              <Note
                key={note.id}
                note={note}
                isDarkTheme={widthViewport > BreakpointMobile}
                isCompactView={true}
                onRemoveNote={() => onRemoveNote({ id: note.id })}
                onEditNote={() => onOpenEditingNote({ note })}
                t={t}
              />
            ))}
      </div>
      {currentUserRole !== 'viewer' && (
        <Form
          form={form}
          initialValues={{
            note: '',
            noteType: 'harvesting',
          }}
          autoComplete="off"
          className={styles.notesForm}
        >
          {editNoteId ? (
            <Button
              className={styles.notesFormEditButton}
              icon={<CroplerIcon name="close" />}
              onClick={() => onCloseEditNote()}
            >
              {t('actions.closeEdit')}
            </Button>
          ) : (
            ''
          )}
          <Form.Item className={styles.notesFormText} name="note">
            <TextArea placeholder={t('notes.addNote')} autoSize />
          </Form.Item>
          <div className={styles.notesFormSelectWrapper}>
            <Form.Item name="noteType" className={styles.notesFormSelect}>
              <Select
                value={NoteTypes.harvesting.id}
                suffixIcon={<CroplerIcon name="down" />}
                className={`${widthViewport > BreakpointTablet ? 'select-dark' : ''}`}
                rootClassName={'select-notes-themes'}
              >
                {ListNotesTypes &&
                  ListNotesTypes.map((item) => {
                    const { id, title, icon } = item;
                    return (
                      <Select.Option key={id} value={id}>
                        <span className={styles.selectItem}>
                          {icon}
                          <span>{title}</span>
                        </span>
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </div>
          <Button
            className={styles.notesFormButton}
            type="primary"
            icon={<CroplerIcon name="send" />}
            onClick={() => onCreateNote()}
          />
        </Form>
      )}
    </div>
  );
};

export default Notes;
